/**
 * Blocked insurer (free use) explain screen
 *
 * Route: `/webAppOnboarding/insurance-blocked-free-process-overview`. No GKV agreement;
 * patient uses Physiofit free.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('blocked', { blockedExplainRouteSegment: free segment })` —
 *   redirects if billing type or blocked-reason variant does not match; `useSaveOnboardingStep(...)`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../styles/colors';
import { space, textSize } from '../../../styles/onboardingUiTokens';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { billingTypeRoutes, readDisplayableBlockedReason } from '../insurance/config';
import { DEFAULT_BLOCKED_EXPLAIN } from '../insurance/copy';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

export function WebOnboardingInsuranceBlockedFreeProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_blocked_free_process_overview');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );
  const blockedReason = readDisplayableBlockedReason(signedUpPatient?.webAppOnboarding?.insuranceInfo?.blockedReason);
  const blockedExplain = ctx.meta.blockedExplain;

  useInsuranceExplainBillingGuard('blocked', {
    blockedExplainRouteSegment: billingTypeRoutes.blocked.explainRouteSegment,
  });

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'blockedFree',
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  };

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      onBack={handleBack}
      forwardLabel="Starten"
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: space.lg,
            paddingBottom: space.xl,
          }}
        >
          <SvgIcon name="checkcircle" size={48} primaryColor={colors.success} />
          <H1>{blockedExplain?.title ?? DEFAULT_BLOCKED_EXPLAIN.title}</H1>
          <Body variant="document" style={{ fontSize: textSize.xl.fontSize, lineHeight: textSize.xl.lineHeight }}>
            {blockedReason ?? blockedExplain?.body ?? DEFAULT_BLOCKED_EXPLAIN.body}
          </Body>
        </div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
